import { connect } from "react-redux";
import { Form } from "formik";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { withRouter } from "react-router-dom";
import * as yup from "yup";

import { sharedRequests } from "@/api/(requests)/shared";

import Button from "../../../UI/Button";
import { CustomForm } from "../../../../component-library/CustomForm";
import { dobValidationSchema } from "../../../../containers/Dashboard/Providers/schema/newPatientValidation";
import FormikInput from "../../../UI/FormikInput";
import FormikInputFormat from "../../../UI/formikMobileInput";
import images from "../../../../utils/images";
import { showAlertWithAction } from "../../../../redux/actions/alerts";
import { STATES } from "../../../../constants/states";
import { SUPPORT_EMAIL, SUPPORT_NUMBER } from "../../../../constants/aptihealth";

const DEMOGRAPHIC_CHECK_USER_AGREEMENT =
  "By clicking the check box below, I consent by electronic signature " +
  "to receive marketing and other calls, text messages (including SMS and MMS messages), and other electronic " +
  "messages (including those made using an automatic telephone dialing system or an artificial or prerecorded " +
  "voice) from aptihealth, healthcare providers with whom I am matched by aptihealth, or any of their agents " +
  "or representatives at the phone number I have provided even if that number is registered on a corporate, " +
  "state, or national do not call registry. I understand that my consent to such calls and messages is not " +
  `required to use aptihealth’s services and that I may contact aptihealth at ${SUPPORT_EMAIL} or ` +
  `${SUPPORT_NUMBER} about its services at any time. I also understand that I may revoke my consent at any time.`;

const DemographicEligibilityCheck = (props) => {
  const { setSharedWorkflowData, sharedWorkflowData } = props;

  const [userAgreementIsChecked, setUserAgreementIsChecked] = useState(false);
  const [eligibleStates, setEligibleStates] = useState(STATES);

  useEffect(() => {
    (async () => {
      const eligibleStatesResponse = await sharedRequests.eligible_states();
      const states = eligibleStatesResponse.eligible_states;

      if (states) {
        states.push("Other");
        setEligibleStates(
          states.map((state) => {
            return { display: state, value: state };
          }),
        );
      }
    })();
  }, []);

  const submitHandler = async (formData, actions) => {
    try {
      const data = {
        dob: moment(formData.dob, "MMDDYYYY").format("MM/DD/YYYY"),
        first_name: formData.first_name,
        last_name: formData.last_name,
        mobile: formData.mobile,
        state_of_residence: formData.state_of_residence,
        user_agreement: userAgreementIsChecked,
        workflow_instance_entity_id: sharedWorkflowData.workflow_instance.entity_id,
        workflow_instance_sort_key: sharedWorkflowData.workflow_instance.sort_key,
      };
      const response = await sharedRequests.demographic_eligibility({ data });

      setSharedWorkflowData({
        ...sharedWorkflowData,
        workflow_instance: response.workflow_instance,
      });
    } catch (e) {
      console.log(e);
      props.showAlertWithAction("Error checking eligibility.");
    }
  };

  const renderForm = (formikProps) => {
    const formikInputProps = {
      errors: formikProps.errors,
      inputClasses: "Input--sm",
      touched: formikProps.touched,
    };

    return (
      <Form style={{ marginBottom: 200 }}>
        <div className="workflow-container-sm">
          <div className="mx-auto px-0 mb-1">
            <div className="px-0 row pb-2">
              <div className="col-12">
                <FormikInput
                  {...formikInputProps}
                  formEl={{
                    elementConfig: {
                      label: "First Name",
                      labelClasses: "no-margin",
                      placeholder: "Enter First Name",
                      type: "text",
                    },
                    elementType: "input",
                    name: "first_name",
                  }}
                />
              </div>

              <div className="col-12">
                <FormikInput
                  {...formikInputProps}
                  formEl={{
                    elementConfig: {
                      label: "Last Name",
                      labelClasses: "no-margin",
                      placeholder: "Enter Last Name",
                      type: "text",
                    },
                    elementType: "input",
                    name: "last_name",
                  }}
                />
              </div>

              {!sharedWorkflowData.workflow_instance.state.dob && (
                <div className="col-12">
                  <FormikInputFormat
                    {...formikInputProps}
                    formEl={{
                      elementConfig: {
                        label: "Date of Birth",
                        labelClasses: "no-margin",
                        placeholder: "MM/DD/YYYY",
                        type: "text",
                      },
                      elementType: "input",
                      name: "dob",
                    }}
                    onChange={async (val) => {
                      await formikProps.setFieldValue("dob", val.value);
                      formikProps.setFieldTouched("dob");
                    }}
                    value={formikProps && formikProps.values["dob"]}
                  />
                </div>
              )}

              <div className="col-12">
                <FormikInput
                  {...formikInputProps}
                  formEl={{
                    elementConfig: {
                      label: "State of Residence",
                      labelClasses: "no-margin",
                      options: eligibleStates,
                      placeholder: "Enter State",
                    },
                    elementType: "select",
                    name: "state_of_residence",
                  }}
                />
              </div>

              <div className="col-12">
                <FormikInputFormat
                  {...formikInputProps}
                  formEl={{
                    elementConfig: {
                      label: "Mobile",
                      labelClasses: "no-margin",
                      placeholder: "Enter Mobile",
                      type: "text",
                    },
                    elementType: "input",
                    name: "mobile",
                  }}
                  onChange={async (val) => {
                    await formikProps.setFieldValue("mobile", val.value);
                    formikProps.setFieldTouched("mobile");
                  }}
                  value={formikProps && formikProps.values["mobile"]}
                />
              </div>

              <div className="col-12">
                <FormikInputFormat
                  {...formikInputProps}
                  formEl={{
                    elementConfig: {
                      label: "Confirm Mobile",
                      labelClasses: "no-margin",
                      placeholder: "Re-enter Mobile",
                      type: "text",
                    },
                    elementType: "input",
                    name: "mobile_validation",
                  }}
                  onChange={async (val) => {
                    await formikProps.setFieldValue("mobile_validation", val.value);
                    formikProps.setFieldTouched("mobile_validation");
                  }}
                  value={formikProps && formikProps.values["mobile_validation"]}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="col-12">
          <div className="scrollable-text-area txt-gry">{DEMOGRAPHIC_CHECK_USER_AGREEMENT}</div>
        </div>

        <div className="workflow-container-sm">
          <div className="mx-auto px-0 mb-1">
            <div className="px-0 row pb-2">
              <div className="col-12 txt-gry">
                <FormikInput
                  {...formikInputProps}
                  formEl={{
                    elementConfig: {
                      displayName: "I agree to the mobile terms listed above",
                      type: "checkbox",
                    },
                    elementType: "input",
                    name: "user_agreement",
                    onclick: () => setUserAgreementIsChecked(!userAgreementIsChecked),
                  }}
                />
              </div>
            </div>

            <div className="d-flex justify-content-between mb-5">
              <Button
                className="Btn Btn--otl-gry Btn--sm-2 workflow-btn"
                onClick={() => props.backHandler()}
                type="button"
              >
                Back
              </Button>
              <Button
                className="Btn Btn--pri Btn--sm-2 workflow-btn"
                disabled={!userAgreementIsChecked ? "disabled" : null}
                type="submit"
              >
                Continue
              </Button>
            </div>
          </div>
        </div>
      </Form>
    );
  };

  return (
    <>
      <div className="p-lg-5 p-3">
        <div className="fs-22 txt-pri mb-3 text-center tw:font-bold">Tell us about yourself</div>
        <div className="text-center mt-3 mb-4">
          <img
            alt="Eligibility Check"
            src={images("./common/workflow/ed-workflow-1-demo-check-active.png")}
          />
        </div>
        {sharedWorkflowData && sharedWorkflowData.workflow_instance && (
          <CustomForm
            initialValues={{
              dob: sharedWorkflowData.workflow_instance.state.dob
                ? moment(sharedWorkflowData.workflow_instance.state.dob, "MM/DD/YYYY").format(
                    "MMDDYYYY",
                  )
                : "",
              first_name: "",
              last_name: "",
              mobile: "",
              mobile_validation: "",
              state_of_residence: "",
              user_agreement: false,
            }}
            onSubmit={submitHandler}
            render={renderForm}
            validationSchema={demographicValidation}
          />
        )}
      </div>
    </>
  );
};

export default connect(null, { showAlertWithAction })(withRouter(DemographicEligibilityCheck));

const demographicValidation = yup.object().shape({
  dob: dobValidationSchema.required("Date of birth is required"),
  first_name: yup
    .string()
    .required("First name is required")
    .matches(/^[a-zA-Z-]+$/, "Numerals, spaces & special characters are not allowed"),
  last_name: yup
    .string()
    .required("Last name is required")
    .matches(/^[a-zA-Z-]+$/, "Numerals, spaces & special characters are not allowed"),
  mobile: yup
    .string()
    .matches(/^\d{10}$/, "Contact number must be a valid US number")
    .required("Mobile number is required"),
  mobile_validation: yup
    .string()
    .matches(/^\d{10}$/, "Contact number must be a valid US number")
    .required("Mobile number is required")
    .test(
      "mobile-match",
      "Mobile number does not match, please check the number",
      function (value) {
        if (!value) {
          return false;
        }

        return this.parent.mobile === value;
      },
    ),
  state_of_residence: yup.string().required("State of residence is required").nullable(),
});
